import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Users, PawPrint, Car, Euro, 
  Calendar, AlertTriangle, ArrowRight, Clock, MapPin,
  Download, Upload, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPanel = ({ password, onBack }) => {
  const [stats, setStats] = useState(null);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [bookingStats, setBookingStats] = useState([]);
  const [popularDestinations, setPopularDestinations] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [membershipOverview, setMembershipOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const headers = { 'X-Admin-Password': password };
      
      const [statsRes, revenueRes, bookingsRes, destRes, activityRes, membershipRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/dashboard/monthly-revenue?months=6`, { headers }),
        axios.get(`${API}/dashboard/booking-stats?months=6`, { headers }),
        axios.get(`${API}/dashboard/popular-destinations?limit=5`, { headers }),
        axios.get(`${API}/dashboard/recent-activity?limit=8`, { headers }),
        axios.get(`${API}/dashboard/membership-overview`, { headers })
      ]);

      setStats(statsRes.data);
      setMonthlyRevenue(revenueRes.data);
      setBookingStats(bookingsRes.data);
      setPopularDestinations(destRes.data);
      setRecentActivity(activityRes.data);
      setMembershipOverview(membershipRes.data);
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const response = await axios.post(`${API}/backup/create`, {}, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success(response.data.message || 'Backup erstellt und per E-Mail gesendet!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Backups');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate max value for chart scaling
  const maxRevenue = Math.max(...monthlyRevenue.map(m => Math.max(m.income, m.expenses)), 1);
  const maxBookings = Math.max(...bookingStats.map(b => b.bookings), 1);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Dashboard wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <button 
                onClick={onBack}
                className="text-blue-100 hover:text-white flex items-center gap-1 mb-2 text-sm"
              >
                ← Zurück zum Admin
              </button>
              <h1 className="text-2xl font-bold">Dashboard</h1>
              <p className="text-blue-100">Übersicht & Statistiken</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handleCreateBackup}
                disabled={isCreatingBackup}
                data-testid="create-backup-btn"
              >
                {isCreatingBackup ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isCreatingBackup ? 'Wird erstellt...' : 'Backup erstellen'}
              </Button>
              <Button variant="secondary" size="sm" onClick={loadAllData}>
                <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Mitglieder</p>
                  <p className="text-3xl font-bold">{stats?.overview?.totalMembers || 0}</p>
                </div>
                <Users className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Tiere</p>
                  <p className="text-3xl font-bold">{stats?.overview?.totalPets || 0}</p>
                </div>
                <PawPrint className="h-10 w-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Umsatz (Monat)</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats?.financial?.revenueThisMonth || 0)}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {stats?.financial?.revenueTrend >= 0 ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                    <span className="text-xs">{stats?.financial?.revenueTrend || 0}%</span>
                  </div>
                </div>
                <Euro className="h-10 w-10 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Buchungen (Monat)</p>
                  <p className="text-3xl font-bold">{stats?.activity?.bookingsThisMonth || 0}</p>
                </div>
                <Car className="h-10 w-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Financial Overview & Alerts */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Financial Summary */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Euro className="h-5 w-5 text-green-600" />
                Finanzübersicht
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Einnahmen</p>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency(stats?.financial?.revenueThisMonth || 0)}
                  </p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600">Ausgaben</p>
                  <p className="text-xl font-bold text-red-600">
                    {formatCurrency(stats?.financial?.expensesThisMonth || 0)}
                  </p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Gewinn</p>
                  <p className={`text-xl font-bold ${(stats?.financial?.profitThisMonth || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {formatCurrency(stats?.financial?.profitThisMonth || 0)}
                  </p>
                </div>
              </div>

              {/* Revenue Chart */}
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-600 mb-3">Umsatz der letzten 6 Monate</p>
                <div className="flex items-end justify-between gap-2 h-32">
                  {monthlyRevenue.map((month, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex gap-1 justify-center items-end h-24">
                        <div 
                          className="w-3 bg-green-500 rounded-t transition-all"
                          style={{ height: `${(month.income / maxRevenue) * 100}%` }}
                          title={`Einnahmen: ${formatCurrency(month.income)}`}
                        />
                        <div 
                          className="w-3 bg-red-400 rounded-t transition-all"
                          style={{ height: `${(month.expenses / maxRevenue) * 100}%` }}
                          title={`Ausgaben: ${formatCurrency(month.expenses)}`}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{month.label?.split(' ')[0]}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded" /> Einnahmen
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-400 rounded" /> Ausgaben
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts & Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Hinweise
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {stats?.financial?.pendingPayments > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="font-medium text-yellow-800">
                      {stats.financial.pendingPayments} offene Zahlungen
                    </span>
                  </div>
                </div>
              )}
              
              {stats?.activity?.expiringMemberships > 0 && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-orange-600" />
                    <span className="font-medium text-orange-800">
                      {stats.activity.expiringMemberships} auslaufende Mitgliedschaften
                    </span>
                  </div>
                </div>
              )}

              {membershipOverview && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">Fahrten-Nutzung</p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full transition-all"
                      style={{ width: `${membershipOverview.utilizationRate}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {membershipOverview.tripsUsedThisYear} von {membershipOverview.tripsUsedThisYear + membershipOverview.tripsAvailableThisYear} Fahrten genutzt
                  </p>
                </div>
              )}

              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500 mb-2">Tägliches Backup</p>
                <p className="text-sm text-gray-700">
                  Automatisch um 00:00 Uhr
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings Chart & Popular Destinations */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Bookings Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5 text-blue-600" />
                Buchungen pro Monat
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between gap-3 h-40">
                {bookingStats.map((month, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex justify-center items-end h-32">
                      <div 
                        className="w-8 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all"
                        style={{ height: `${(month.bookings / maxBookings) * 100}%`, minHeight: month.bookings > 0 ? '10%' : '0' }}
                      />
                    </div>
                    <span className="text-lg font-bold text-gray-700">{month.bookings}</span>
                    <span className="text-xs text-gray-500">{month.label?.split(' ')[0]}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Popular Destinations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-500" />
                Beliebte Ziele
              </CardTitle>
            </CardHeader>
            <CardContent>
              {popularDestinations.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Noch keine Daten verfügbar</p>
              ) : (
                <div className="space-y-3">
                  {popularDestinations.map((dest, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-300 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{dest.destination}</p>
                      </div>
                      <span className="text-sm text-gray-600">{dest.count}x</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-600" />
              Letzte Aktivitäten
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Noch keine Aktivitäten</p>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      activity.type === 'booking' ? 'bg-blue-100 text-blue-600' :
                      activity.type === 'member' ? 'bg-green-100 text-green-600' :
                      'bg-yellow-100 text-yellow-600'
                    }`}>
                      {activity.type === 'booking' && <Car className="h-5 w-5" />}
                      {activity.type === 'member' && <Users className="h-5 w-5" />}
                      {activity.type === 'income' && <Euro className="h-5 w-5" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{activity.title}</p>
                      <p className="text-sm text-gray-600">{activity.description}</p>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(activity.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPanel;
